import * as React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@nohara/react-theme';
import { OidcConfig, OidcProvider } from '@nohara/react-oidc';
import { NotificationsProvider } from '@toolpad/core/useNotifications';
import { I18nextProvider } from 'react-i18next';
import { queryConfig } from '@nohara/react-core';

import { MainErrorFallback } from '@/components/errors/main';
import { env } from '@/config/env';
import { LoadingScreen } from '@/components/ui';
import i18n from '@/i18n/i18n';

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
      })
  );

  const [oidcConfig] = React.useState<OidcConfig>(() => ({
    apiEndpoint: env.API_ENDPOINT,
    oauthEndpoint: env.OAUTH_DOMAIN,
    userPoolId: env.USER_POOL_ID,
    userPoolWebClientId: env.USER_POOL_WEB_CLIENT_ID,
    oidcCallbackPath: env.OIDC_CALLBACK_PATH,
    signOutPath: env.SIGN_OUT_PATH,
  }));

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <I18nextProvider i18n={i18n}>
        <NotificationsProvider
          slotProps={{
            snackbar: {
              anchorOrigin: { vertical: 'top', horizontal: 'right' },
              autoHideDuration: 3000,
            },
          }}
        >
          <ErrorBoundary FallbackComponent={MainErrorFallback}>
            <HelmetProvider>
              <QueryClientProvider client={queryClient}>
                {import.meta.env.DEV && <ReactQueryDevtools />}
                <ThemeProvider>
                  <OidcProvider config={oidcConfig}>{children}</OidcProvider>
                </ThemeProvider>
              </QueryClientProvider>
            </HelmetProvider>
          </ErrorBoundary>
        </NotificationsProvider>
      </I18nextProvider>
    </React.Suspense>
  );
};
